/**
 * Snacio Personalization Engine v1.0
 * No-login personalization: name, behavior, location, session intelligence
 * Drop in: assets/snacio-persona.js
 */

(function () {
  'use strict';

  const KEYS = {
    NAME:       'snacio_name',
    VIEWED:     'snacio_viewed',
    BEHAVIOR:   'snacio_behavior',
    SESSION:    'snacio_session',
    NAME_ASKED: 'snacio_name_asked',
  };

  const SP = {

    /* ─────────────────────── INIT ─────────────────────── */

    init() {
      this._load();
      this._updateVisit();
      this._trackCurrentPage();
      this._scheduleNameModal();
      this._dispatch('snacio:ready', this.context());
    },

    /* ─────────────────────── LOAD / SAVE ─────────────────────── */

    _load() {
      this.name     = localStorage.getItem(KEYS.NAME) || null;
      this.viewed   = this._parse(localStorage.getItem(KEYS.VIEWED), []);
      this.behavior = this._parse(localStorage.getItem(KEYS.BEHAVIOR), {
        categories: {},
        visitCount: 0,
        lastVisit: null,
        avgSessionPages: 0,
        totalPageViews: 0,
      });
      this.session  = this._parse(sessionStorage.getItem(KEYS.SESSION), {
        pageViews: 0,
        start: Date.now(),
        fresh: true,
      });
    },

    _save() {
      try {
        localStorage.setItem(KEYS.VIEWED,   JSON.stringify(this.viewed));
        localStorage.setItem(KEYS.BEHAVIOR, JSON.stringify(this.behavior));
        if (this.name)     localStorage.setItem(KEYS.NAME, this.name);
        sessionStorage.setItem(KEYS.SESSION, JSON.stringify(this.session));
      } catch (e) { /* storage quota — silent */ }
    },

    /* ─────────────────────── VISIT TRACKING ─────────────────────── */

    _updateVisit() {
      const b = this.behavior;
      const s = this.session;

      if (s.fresh) {
        b.visitCount = (b.visitCount || 0) + 1;
        b.lastVisit  = new Date().toISOString();
        s.fresh      = false;
      }

      s.pageViews = (s.pageViews || 0) + 1;
      b.totalPageViews = (b.totalPageViews || 0) + 1;
      this._save();
    },

    /* ─────────────────────── PAGE TRACKING ─────────────────────── */

    _trackCurrentPage() {
      // Product data injected by snacio-init.liquid
      if (window.__snacioProd) this.trackProduct(window.__snacioProd);
      if (window.__snacioCol)  this.trackCategory(window.__snacioCol);
    },

    trackProduct(product) {
      if (!product || !product.id) return;

      // Remove duplicate
      this.viewed = this.viewed.filter(p => p.id !== product.id);

      // Prepend
      this.viewed.unshift({
        id:     product.id,
        handle: product.handle,
        title:  product.title,
        image:  product.image,
        url:    product.url,
        price:  product.price,
        ts:     Date.now(),
      });

      // Cap at 16 items
      this.viewed = this.viewed.slice(0, 16);

      // Category weight
      if (product.type) this.trackCategory(product.type);

      this._save();
      this._dispatch('snacio:product-tracked', product);
    },

    trackCategory(cat) {
      if (!cat) return;
      const c = this.behavior.categories;
      c[cat] = (c[cat] || 0) + 1;
      this._save();
    },

    /* ─────────────────────── NAME ─────────────────────── */

    setName(name) {
      if (!name || !name.trim()) return;
      this.name = name.trim();
      localStorage.setItem(KEYS.NAME, this.name);
      this._dispatch('snacio:name-set', { name: this.name });
      this._dispatch('snacio:ready', this.context()); // re-broadcast
    },

    _scheduleNameModal() {
      if (this.name) return;
      if (localStorage.getItem(KEYS.NAME_ASKED)) return;

      // Ask after 10s on first visit
      setTimeout(() => {
        this._dispatch('snacio:show-modal');
        localStorage.setItem(KEYS.NAME_ASKED, '1');
      }, 10000);
    },



    /* ─────────────────────── COMPUTED DATA ─────────────────────── */

    greeting() {
      const h = new Date().getHours();
      if (h >= 5  && h < 12) return 'Good morning';
      if (h >= 12 && h < 17) return 'Good afternoon';
      if (h >= 17 && h < 21) return 'Good evening';
      return 'Welcome back';
    },

    isReturning() {
      return (this.behavior.visitCount || 0) > 1;
    },

    daysSinceLast() {
      if (!this.behavior.lastVisit) return null;
      const ms = Date.now() - new Date(this.behavior.lastVisit).getTime();
      return Math.floor(ms / 86400000);
    },

    topCategory() {
      const cats = this.behavior.categories || {};
      const entries = Object.entries(cats);
      if (!entries.length) return null;
      return entries.sort((a, b) => b[1] - a[1])[0][0];
    },

    lastViewedId() {
      return this.viewed.length ? this.viewed[0].id : null;
    },

    /* ─────────────────────── CONTEXT OBJECT ─────────────────────── */

    context() {
      return {
        name:           this.name,
        greeting:       this.greeting(),
        isReturning:    this.isReturning(),
        visitCount:     this.behavior.visitCount || 1,
        daysSinceLast:  this.daysSinceLast(),
        topCategory:    this.topCategory(),
        recentlyViewed: this.viewed,
        sessionPages:   this.session.pageViews,
        lastViewedId:   this.lastViewedId(),
      };
    },

    /* ─────────────────────── RECOMMENDATIONS ─────────────────────── */

    async fetchRecommendations(productId, limit = 4) {
      if (!productId) return [];
      try {
        const url  = `/recommendations/products.json?product_id=${productId}&limit=${limit}`;
        const res  = await fetch(url);
        const data = await res.json();
        return data.products || [];
      } catch (_) { return []; }
    },

    /* ─────────────────────── UTILS ─────────────────────── */

    _dispatch(event, detail = {}) {
      document.dispatchEvent(new CustomEvent(event, { detail, bubbles: true }));
    },

    _parse(str, fallback) {
      try { return JSON.parse(str) || fallback; }
      catch (_) { return fallback; }
    },

  };

  // Expose globally
  window.SnacioPersna = SP;

  // Auto-init after DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => SP.init());
  } else {
    SP.init();
  }

})();
